/**
 * The admin area's navigation model — groups, pages, and how an old link
 * finds its page.
 *
 * Two levels (2026-10-07). Seventeen pages in one row scrolled sideways on
 * every screen narrower than a desktop, and grouping them with separators in
 * that same row did not stop it. So: a short row of groups, and the selected
 * group's pages under it. Both rows wrap rather than scroll, so nothing is
 * ever off-screen at phone width.
 *
 * Groups follow the job being done. Order within a group is most-used
 * first. Labels are sentence case.
 *
 * Every page keeps the id it had, so `#/admin?tab=<id>` links — bookmarks,
 * the runbook, the to-do list, other tabs' "open in …" links — still land.
 * `resolveTab` maps the ids and params that no longer name a page.
 */

export type Tab =
  | "overview"
  | "assistants"
  | "teams"
  | "scheduled-jobs"
  | "glossary"
  | "ai-requests"
  | "access-requests"
  | "people"
  | "feedback-review"
  | "data-sources"
  | "platform-jobs"
  | "governance"
  | "evidence"
  | "audit"
  | "usage"
  | "budgets"
  | "logs";

export type GroupId =
  | "overview"
  | "assistants"
  | "people"
  | "data"
  | "governance"
  | "cost"
  | "system";

export interface NavTab {
  id: Tab;
  label: string;
  /** Tooltip on the count badge, for tabs that have a queue. */
  countTitle?: (n: number) => string;
}

export interface NavGroup {
  id: GroupId;
  label: string;
  tabs: NavTab[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    id: "overview",
    label: "Overview",
    tabs: [{ id: "overview", label: "Overview" }],
  },
  {
    id: "assistants",
    label: "Assistants",
    tabs: [
      { id: "assistants", label: "Assistants" },
      { id: "teams", label: "Teams" },
      { id: "scheduled-jobs", label: "Scheduled jobs" },
      { id: "glossary", label: "Glossary" },
      {
        id: "ai-requests",
        label: "AI Board queue",
        countTitle: (n) => `${n} request(s) awaiting a decision`,
      },
      {
        id: "access-requests",
        label: "Access requests",
        countTitle: (n) => `${n} access request(s) open`,
      },
    ],
  },
  {
    id: "people",
    label: "People",
    tabs: [
      { id: "people", label: "People" },
      {
        id: "feedback-review",
        label: "Feedback review",
        countTitle: (n) => `${n} thumbs-down not yet analyzed`,
      },
    ],
  },
  {
    id: "data",
    label: "Data",
    tabs: [
      { id: "data-sources", label: "Data sources" },
      { id: "platform-jobs", label: "Platform jobs" },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    tabs: [
      { id: "governance", label: "Approvals" },
      { id: "evidence", label: "Evidence" },
      { id: "audit", label: "Audit" },
    ],
  },
  {
    id: "cost",
    label: "Usage & cost",
    tabs: [
      { id: "usage", label: "Usage" },
      { id: "budgets", label: "Budgets" },
    ],
  },
  { id: "system", label: "System", tabs: [{ id: "logs", label: "Logs" }] },
];

const ALL_TABS = new Set<string>(
  NAV_GROUPS.flatMap((g) => g.tabs.map((t) => t.id)),
);

/**
 * Pages a viewer does not get (ADR 0072): their backing routes are
 * admin-only — raw log lines, feedback-review runs that quote conversations,
 * people's own free-text reasons for asking — so the page would be nothing
 * but a 403. The backend refuses them regardless; this keeps the nav honest.
 * A group left with no page is hidden with them.
 */
export const VIEWER_HIDDEN = new Set<Tab>([
  "logs",
  "feedback-review",
  "access-requests",
]);

export function visibleGroups(readOnly: boolean): NavGroup[] {
  if (!readOnly) return NAV_GROUPS;
  return NAV_GROUPS.map((g) => ({
    ...g,
    tabs: g.tabs.filter((t) => !VIEWER_HIDDEN.has(t.id)),
  })).filter((g) => g.tabs.length > 0);
}

export function groupOf(tab: Tab): GroupId {
  return NAV_GROUPS.find((g) => g.tabs.some((t) => t.id === tab))!.id;
}

/**
 * The page an admin hash names. Old ids and params keep working:
 *
 *   tab=assistants&view=teams → teams   (Teams was a toggle inside Assistants)
 *   tab=jobs                  → platform-jobs   (the pre-ADR 0055 Jobs tab)
 *   tab=beta-signups          → access-requests (retired for Access requests)
 *
 * Anything else unknown — or no tab — is the Overview.
 */
export function resolveTab(hash: string): Tab {
  const q = new URLSearchParams(hash.split("?")[1] ?? "");
  const raw = q.get("tab") ?? "";
  if (raw === "assistants" && q.get("view") === "teams") return "teams";
  if (raw === "jobs") return "platform-jobs";
  if (raw === "beta-signups") return "access-requests";
  return ALL_TABS.has(raw) ? (raw as Tab) : "overview";
}
